"use client";
import {
  type ChangeEventHandler,
  type InputHTMLAttributes,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type Ref,
  useRef,
  useState,
  type FC,
} from "react";
import { isEmpty, mapObject, mergeClasses, rem } from "@huub-web/utils";

import { countries, type CountryData } from "../../data/countries";

import { Popup, type PopupProps } from "../Popup";
import {
  SelectList,
  type SelectListOption,
  type SelectListSelectHandler,
} from "../SelectList";
import { BaseInputField, type BaseInputFieldProps } from "../BaseInputField";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { mergeRefs } from "../../utils/mergeRefs";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useFormValidation } from "../../hooks/useFormValidation";
import type { ValidatableProps } from "../../props";

import { inputStyle } from "../BaseInputField/BaseInputField.css";
import { typography } from "../../theme/css/typography.css";
import { activeOptionStyle, iconStyle } from "./CountryField.css";

export type CountryFieldChangeHandler = (value: string) => void;

export type CountryFieldProps = BaseInputFieldProps &
  Pick<InputHTMLAttributes<HTMLInputElement>, "placeholder"> &
  ValidatableProps & {
    value?: string;
    defaultValue?: string;
    popup?: Pick<PopupProps, "placement" | "minWidth" | "maxWidth" | "width">;
    inputRef?: Ref<HTMLInputElement>;
    onOutsideClick?: () => void;
    onChange?: CountryFieldChangeHandler;
  };

/**
 *  CountryField component is a dropdown component that allows users to CountryField a single option from a list of options.
 */
export const CountryField: FC<CountryFieldProps> = ({
  id: idProp,
  className,
  placeholder,
  inputRef,
  disabled: disabledProp,
  value,
  defaultValue,
  display,
  popup,
  variant = "default",
  autoFocus = false,
  leading,
  error: errorProp,
  onChange,
  onClick,
  onOutsideClick,
  validation,
  name,
  ref,
  ...props
}) => {
  const proxyRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const [isActive, setIsActive] = useState(autoFocus);

  const { id, disabled, error } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
    ref: proxyRef,
  });

  useClickOutside([popupRef, fieldRef], () => {
    closePopup();
    onOutsideClick?.();
  });

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    onClick?.(e);
    openPopup();
  };

  const openPopup = () => {
    if (disabled) {
      return;
    }
    setIsActive(true);
  };

  const handleClose = () => {
    closePopup();
  };

  const closePopup = () => {
    if (isActive && proxyRef.current) {
      proxyRef.current.focus();
    }
    setIsActive(false);
  };

  const handleProxyInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    console.warn("handleInputChange", e.target.value);
  };

  const handleProxyKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      openPopup();
    }
  };

  const options: SelectListOption[] = mapObject(
    countries,
    ([code, country]) => ({
      label: country.dutchName,
      value: code as string,
    }),
  ).sort((a, b) => a.label.localeCompare(b.label));

  const selectedCountry: CountryData | undefined = countries?.[value as string];

  const handleSelect: SelectListSelectHandler = (option) => {
    onChange?.(option.value as string);
    closePopup();
  };

  return (
    <Popup
      ref={popupRef}
      active={isActive}
      onClose={handleClose}
      applyActive
      escape
      size="minimal"
      content={
        <SelectList
          search
          value={value}
          options={options}
          onSelect={handleSelect}
        />
      }
      variant={variant === "toggle" ? "toggle" : undefined}
      {...popup}
      minWidth={popup?.minWidth ?? rem(240)}
      maxWidth={popup?.maxWidth ?? rem(240)}
      placement={popup?.placement ?? "bottomRight"}
    >
      <BaseInputField
        // ref = is being overruled by popup
        altRef={mergeRefs(ref, fieldRef)}
        onClick={handleClick}
        disabled={disabled}
        leading={leading}
        width="stretch"
        labelFloat={isEmpty(value) && !isActive && !placeholder}
        error={error}
        trailingLast={
          variant === "default" ? (
            <Icon
              className={iconStyle({ isOpen: isActive })}
              icon="chevronDown"
              size="s2"
            />
          ) : undefined
        }
        placeholder={
          isEmpty(value) && placeholder && !display ? placeholder : undefined
        }
        display={display}
        variant={variant}
        proxy={
          <input
            ref={mergeRefs(proxyRef, inputRef)}
            id={id}
            className={mergeClasses(
              typography.bodyS,
              inputStyle({ proxy: true, disabled }),
              className,
            )}
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            onKeyDown={handleProxyKeyDown}
            onChange={handleProxyInputChange}
          />
        }
        {...props}
      >
        {selectedCountry && (
          <div className={mergeClasses(activeOptionStyle())}>
            <Text variant={variant === "toggle" ? "bodyL" : "bodyM"} ellipsis>
              {selectedCountry.dutchName}
            </Text>
          </div>
        )}
      </BaseInputField>
    </Popup>
  );
};
